import { useEffect, useState } from "react"
import {
  Center,
  Heading,
  Spinner,
  useToast,
} from "@chakra-ui/react"
import { NavLayout } from "../components/NavLayout"
import useAxiosPrivate from "../hooks/useAxiosPrivate"
import { useNavigate, useParams } from "react-router-dom"
import { LeadSourceForm } from "../components/Forms/LeadSourceForm"
import { addServerErrors } from "../services/add-server-errors"

type FormData = {
  id: string
  name: string
  active: boolean
}

type Errors = {
  [key: string]: { message: string }
}

const initialErrors: Errors = {}

const initialFormData: FormData = {
  id: "",
  name: "",
  active: true,
}

export const EditLeadSource = () => {
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const toast = useToast()
  const { leadSourceId } = useParams()
  const [data, setData] = useState(initialFormData)
  const [errors, setErrors] = useState(initialErrors)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const endpoint = `/lead-sources/edit/${leadSourceId}`

  useEffect(() => {
    setLoading(true)
    let isMounted = true
    const controller = new AbortController()

    const getData = async () => {
        try {
          const response = await axiosPrivate.get(endpoint, {
            signal: controller.signal
          })
          isMounted && setData(response.data.leadSource)
          setLoading(false)
        } catch (err: any) {
          console.error(err)
          toast({
            title: 'Error',
            description: err.message,
            status: 'error',
            isClosable: true,
          })
          navigate(-1)
        }
    }

    const timeout = setTimeout(getData, 500)

    return () => {
        isMounted = false
        isMounted && controller.abort()
        clearTimeout(timeout)
    }
  }, [axiosPrivate, endpoint, navigate, toast])

  const handleSubmit = async (formData: FormData) => {
    setSubmitting(true)
    try {
      const response = await axiosPrivate.post(endpoint, formData)

      if (!response?.data?.leadSource?.errors) {
        toast({
          title: 'Success',
          description: 'Lead Source successfully updated!',
          status: 'success',
          isClosable: true,
        })
        navigate(-1)
      } else {
        addServerErrors(response?.data?.leadSource?.errors, setErrors)
        toast({
          title: 'Error',
          description: 'Please review any errors and resubmit',
          status: 'error',
          isClosable: true,
        })
      }
    } catch (e: any) {
      toast({
        title: 'Error',
        description: e.message,
        status: 'error',
        isClosable: true,
      })
    }
    setSubmitting(false)
  }

  return (
    <NavLayout
      left={
        <Heading as="h1" size="lg">Edit Lead Source</Heading>
      }
    >
      {!loading
        ? (
          <LeadSourceForm edit={true} errors={errors} handleSubmit={handleSubmit} initialFormData={data} loading={submitting} />
        ) : (
          <Center>
            <Spinner
              thickness='4px'
              speed='0.65s'
              emptyColor='gray.200'
              color='blue.500'
              size='xl'
            />
          </Center>
        )
      }
    </NavLayout>
  )
}
